<template>
    <div
        id="rphq-embed-main"
        class="overflow-hidden p-1"
    >
        <prime-button
            v-if="showBackButton"
            class="-mb-5 !px-2 !py-1"
            icon="pi pi-chevron-left"
            :label="t('back')"
            @click="router.go(-1)"
        />

        <slot />

        <powered-by-footer class="mt-6" />
    </div>
</template>

<script setup lang="ts">
import { useSetViewer } from '~/composables/use-login'
import {
    type ActiveFacility,
    ActiveFacilityFragment,
    provideActiveFacility
} from '~/composables/use-active-facility'
import { computed, onBeforeUnmount, provide, ref } from 'vue'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { getFragmentData, graphql } from '~/resources/graphql'
import { createError, useRoute, useState } from '#app'
import { useHead } from '@unhead/vue'
import { provideEmbedParams } from '~/composables/use-embed-params'
import { useRouter } from 'vue-router'
import PrimeButton from 'primevue/button'
import PoweredByFooter from '~/components/PoweredByFooter.vue'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })
const selectedLanguage = useSelectedLanguage()

useHead({
    script: {
        type: 'text/javascript',
        tagPosition: 'bodyOpen',
        innerHTML:
            'setInterval(() => {\n' +
            "  const rphqEmbedMain = document.getElementById('rphq-embed-main');\n" +
            '  if (rphqEmbedMain !== null) {\n' +
            "    parent.postMessage('RPHQ::1::' + JSON.stringify({" +
            "      key: 'iframe-resize',\n" +
            '      height: rphqEmbedMain.scrollHeight\n' +
            "    }), '*');\n" +
            '  }\n' +
            '}, 50);'
    }
})

provideEmbedParams()
const router = useRouter()
const route = useRoute()

const showBackButton = computed(() => route.query.origin !== route.params.slug)

const facilitySlug = useState<string>('facilityParamSlug')
const setViewer = useSetViewer()
const query = useRpQuery({ orgLevel: true })
const { data, suspense } = useQuery({
    queryKey: ['EmbedLayoutQuery', facilitySlug],
    placeholderData: previousData => previousData,
    queryFn: () => {
        return query(
            graphql(/* GraphQL */ `
                query EmbedLayoutQuery(
                    $facilitySlug: String!
                    $storefrontConfigInput: StorefrontConfigInput!
                ) {
                    storefrontConfig(input: $storefrontConfigInput) {
                        root
                    }
                    viewer {
                        ...ViewerFragment
                    }
                    facility(slug: $facilitySlug) {
                        ...ActiveFacilityFragment
                    }
                }
            `),
            {
                facilitySlug: facilitySlug.value,
                storefrontConfigInput: {
                    facilitySlug: facilitySlug.value,
                    language: selectedLanguage.value
                }
            }
        )
    }
})

await suspense()
if (data.value !== undefined) {
    if (data.value.viewer !== null) {
        setViewer(data.value.viewer)
    } else {
        setViewer(undefined)
    }
}

const activeFacility = computed<ActiveFacility>(() => {
    const result = data.value?.facility
    if (result === undefined || result === null) {
        throw createError({
            statusCode: 404,
            statusMessage: t('layout.pageNotFound')
        })
    }

    return getFragmentData(ActiveFacilityFragment, result)
})
provideActiveFacility(activeFacility)

const storefrontPrefix = computed(() => `/${activeFacility.value.slug}`)
const catalog = computed(() => {
    if (
        data.value?.storefrontConfig === undefined ||
        data.value?.storefrontConfig === null
    ) {
        throw createError({
            statusCode: 404,
            statusMessage: t('layout.pageNotFound')
        })
    }

    return data.value.storefrontConfig.root
})
provide('CATALOG', catalog)
provide('STOREFRONT_PREFIX', storefrontPrefix)

const unmount = ref(false)
const headStyle = computed(() => {
    if (unmount.value) {
        return []
    }

    return activeFacility.value.branding.styleString
})
const headLinks = computed(() => {
    return activeFacility.value.organization.branding.headLinks
})

useHead({
    style: headStyle,
    link: headLinks
})

onBeforeUnmount(() => {
    // Clear styles when leaving the component
    unmount.value = true
})
</script>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

#__nuxt {
    font-size: 13px;
}
</style>
